// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What this device can actually do (M3-35, 08 §6).
//
// 08 §6 opens with the rule the rest of this file exists to keep: **no user-agent sniffing.**
// A user-agent string is a claim about identity, and identity is a poor proxy for capability —
// two laptops with the same string differ by a discrete GPU, the same phone differs by three
// years of thermal paste, and every browser that ever lied in that header lied because
// somebody wrote the rule this one refuses to write.
//
// So the tier is chosen from measurement, in two halves that are deliberately separate:
//
//   * **A probe**, here. Cheap, synchronous, and available before anything is on screen — which
//     matters, because the audio voice pool and the scene build both need a tier *before* there
//     is a first frame to measure. The probe cannot tell a fast GPU from a slow one; what it
//     can do is rule tiers out, so it produces a **ceiling** rather than an answer.
//   * **A measurement**, `tierForFrameMs`. Once there is a machine on screen it can be timed,
//     and a number in milliseconds settles what a renderer string can only hint at.
//
// The probe's job is to be *pessimistic and certain*; the measurement's job is to be *accurate*.
// Neither is asked to do the other's work, which is the mistake that produces a detection table
// full of device names.

import { RENDER_TIERS, TIER_BUDGETS, type RenderTier } from './tiers'

/**
 * The largest texture any shipped machine asks for, in texels on its longest side.
 *
 * NEON NOVA's playfield art is 1024 × 2048 and its cabinet sides are 2048 × 512. A device whose
 * `MAX_TEXTURE_SIZE` is below this cannot upload the art at all — not slowly, not badly: the
 * upload is a GL error and the surface is black. WebGL2 guarantees at least 2048, so in
 * practice this is unreachable while `webgl2` is true, and it is checked anyway because "in
 * practice" is how a black playfield ships.
 */
export const LARGEST_ART_TEXTURE = 2048

/** Below this, a device is treated as a phone-class GPU whatever else it claims. */
export const COMFORTABLE_TEXTURE_SIZE = 4096

/** Fewer cores than this and the main thread has nowhere to put a 1000 Hz simulation. */
export const COMFORTABLE_CORES = 4

/** Gigabytes of device memory below which the top tier's textures are not worth attempting. */
export const COMFORTABLE_MEMORY_GB = 4

/**
 * Renderer substrings that mean there is no GPU behind this context.
 *
 * A software rasteriser reports WebGL2, a large `MAX_TEXTURE_SIZE` and every extension it is
 * asked for, and then draws at two frames a second. It is the one case where the renderer
 * string is not a hint but a fact, and the one case the calibration measurement would also
 * catch — three seconds later, after the player has already seen it.
 */
export const SOFTWARE_RENDERERS: readonly string[] = [
  'swiftshader',
  'llvmpipe',
  'softpipe',
  'software rasterizer',
  'microsoft basic render',
]

/** What the probe found. Every field is a measurement or an absence, never a guess at a name. */
export interface Capability {
  /** Is there a WebGL2 context to be had at all? */
  readonly webgl2: boolean
  /** `MAX_TEXTURE_SIZE`, or 0 when there was no context to ask. */
  readonly maxTextureSize: number
  /**
   * The unmasked renderer string, lower-cased, or `''`.
   *
   * A **weak hint** (08 §6's own word) and treated as one everywhere except software
   * rasterisers. Most browsers now mask it behind a privacy setting, so code that needs it to
   * be present is code that is about to be wrong on Firefox.
   */
  readonly renderer: string
  /** `navigator.deviceMemory` in GiB, where the browser reports it. Chrome-family only. */
  readonly deviceMemoryGb: number | undefined
  /** `navigator.hardwareConcurrency`, where the browser reports it. */
  readonly cores: number | undefined
  /** `(pointer: coarse)` — a touch screen, and with it a battery and a thermal ceiling. */
  readonly coarsePointer: boolean
  /** True only while actually presenting in a headset. Not "the browser supports WebXR". */
  readonly xr: boolean
}

/**
 * The narrow slice of the browser the probe touches, so a test can supply all of it.
 *
 * A flat shape rather than a `Window`, deliberately: `createElement` lives on `document` and the
 * other three live on `window`, and a default of "just pass `globalThis`" therefore silently
 * finds no `createElement` at all — which reports no WebGL2, on every browser, and pins every
 * device in the world to `low`. Nothing throws and nothing looks wrong. `browserProbeHost`
 * assembles the four in one place so there is only one thing to get right.
 */
export interface ProbeHost {
  createElement?: (tag: string) => unknown
  navigator?: {
    deviceMemory?: number
    hardwareConcurrency?: number
  }
  matchMedia?: (query: string) => { matches: boolean }
  /** True while the page is presenting in a headset. Supplied by the XR session, not guessed. */
  xr?: boolean
}

interface ProbeCanvas {
  width: number
  height: number
  getContext: (id: string, opts?: unknown) => unknown
}

interface ProbeContext {
  getParameter: (p: number) => unknown
  getExtension: (name: string) => Record<string, unknown> | null
  MAX_TEXTURE_SIZE: number
}

/**
 * Ask the device what it is.
 *
 * Creates a throwaway 1 × 1 canvas, takes a WebGL2 context, reads two numbers off it and drops
 * it. Deliberately **not** the machine's canvas: this runs before the scene exists — the audio
 * voice pool needs a tier at the first user gesture, which may be long before anybody presses
 * the button that builds a renderer.
 *
 * Everything is guarded. A probe that throws is a product that will not start, and every line
 * here touches an API some browser somewhere has removed, renamed or put behind a flag.
 */
export function browserProbeHost(): ProbeHost {
  const global = globalThis as {
    document?: { createElement: (tag: string) => unknown }
    navigator?: { deviceMemory?: number; hardwareConcurrency?: number }
    matchMedia?: (query: string) => { matches: boolean }
  }
  const host: ProbeHost = {}
  const doc = global.document
  if (doc !== undefined) host.createElement = (tag) => doc.createElement(tag)
  if (global.navigator !== undefined) host.navigator = global.navigator
  if (global.matchMedia !== undefined) host.matchMedia = (q) => global.matchMedia!(q)
  return host
}

export function probeCapability(host: ProbeHost = browserProbeHost()): Capability {
  let webgl2 = false
  let maxTextureSize = 0
  let renderer = ''

  try {
    const canvas = host.createElement?.('canvas') as ProbeCanvas | undefined
    if (canvas !== undefined && typeof canvas.getContext === 'function') {
      canvas.width = 1
      canvas.height = 1
      // `failIfMajorPerformanceCaveat` is left false here on purpose. Asking the probe to fail
      // on a software context would tell us less, not more: the game would rather draw slowly
      // than not at all, so what is wanted is the caveat *reported*, not the context refused.
      const gl = canvas.getContext('webgl2', { failIfMajorPerformanceCaveat: false }) as
        | ProbeContext
        | null
        | undefined
      if (gl !== null && gl !== undefined) {
        webgl2 = true
        const size = gl.getParameter(gl.MAX_TEXTURE_SIZE)
        maxTextureSize = typeof size === 'number' && Number.isFinite(size) ? size : 0
        const debug = gl.getExtension('WEBGL_debug_renderer_info')
        const key = debug?.['UNMASKED_RENDERER_WEBGL']
        if (typeof key === 'number') {
          const name = gl.getParameter(key)
          if (typeof name === 'string') renderer = name.toLowerCase()
        }
        // Three has no use for this context and a browser will not hand out an unbounded number
        // of them — around sixteen, after which the oldest is silently killed. Losing the
        // *machine's* context to a probe nobody released would be a black canvas with no cause.
        const lose = gl.getExtension('WEBGL_lose_context')
        const release = lose?.['loseContext']
        if (typeof release === 'function') (release as () => void).call(lose)
      }
    }
  } catch {
    // Left at the defaults. A browser that throws on `getContext` is one this product cannot
    // draw on, which is exactly what `webgl2: false` says.
  }

  const memory = host.navigator?.deviceMemory
  const cores = host.navigator?.hardwareConcurrency
  return {
    webgl2,
    maxTextureSize,
    renderer,
    deviceMemoryGb: typeof memory === 'number' && memory > 0 ? memory : undefined,
    cores: typeof cores === 'number' && cores > 0 ? cores : undefined,
    coarsePointer: host.matchMedia?.('(pointer: coarse)').matches === true,
    xr: host.xr === true,
  }
}

/** Is this a renderer with no GPU behind it? */
export function isSoftwareRenderer(renderer: string): boolean {
  const name = renderer.toLowerCase()
  return SOFTWARE_RENDERERS.some((mark) => name.includes(mark))
}

/** The highest tier a device may run, and every reason it is not higher. */
export interface TierCeiling {
  readonly ceiling: RenderTier
  /** One short phrase per constraint, in the order applied. The settings panel shows these. */
  readonly reasons: readonly string[]
}

/**
 * The highest tier the probe will allow.
 *
 * A ceiling, not a choice: nothing here can tell a fast GPU from a slow one, so nothing here
 * ever picks `high` — it only declines to. The measurement picks.
 *
 * The rules, and why each is a ceiling rather than a hint:
 *
 *   * **In a headset the tier is `xr`.** Not a rung on a ladder; a different set of constraints
 *     — two eyes, 72–90 Hz, a mobile SoC — and the only tier with light probes in it.
 *   * **No WebGL2, or a texture limit under the art**, and the machine cannot be drawn as
 *     authored. `low` is the floor and there is nothing below it to fall to.
 *   * **A software rasteriser** is `low` on the strength of the string, because the alternative
 *     is three seconds of two-frames-a-second before the governor reaches the same conclusion.
 *   * **A coarse pointer** caps at `medium`. This is the rule most likely to be argued with — a
 *     recent tablet will out-render a five-year-old laptop — and it stands because the failure
 *     it prevents is invisible in a benchmark: a phone renders `high` beautifully for ninety
 *     seconds and then thermally throttles for the rest of the game. A machine that gets worse
 *     while you play it is worse than one that started a step lower.
 */
export function tierCeiling(cap: Capability): TierCeiling {
  if (cap.xr) return { ceiling: 'xr', reasons: ['presenting in a headset'] }

  const reasons: string[] = []
  let ceiling: RenderTier = 'high'
  const lower = (to: RenderTier, why: string): void => {
    reasons.push(why)
    // `RENDER_TIERS` runs best-to-worst, so a larger index is a lower tier. Never rises: a
    // second constraint cannot undo the first, which is what makes the order here irrelevant.
    if (RENDER_TIERS.indexOf(to) > RENDER_TIERS.indexOf(ceiling)) ceiling = to
  }

  if (!cap.webgl2) lower('low', 'no WebGL2')
  if (cap.webgl2 && cap.maxTextureSize < LARGEST_ART_TEXTURE) {
    lower('low', `textures limited to ${cap.maxTextureSize}, the art needs ${LARGEST_ART_TEXTURE}`)
  }
  if (isSoftwareRenderer(cap.renderer)) lower('low', 'software renderer')
  if (cap.webgl2 && cap.maxTextureSize >= LARGEST_ART_TEXTURE && cap.maxTextureSize < COMFORTABLE_TEXTURE_SIZE) {
    lower('medium', `small texture limit (${cap.maxTextureSize})`)
  }
  if (cap.cores !== undefined && cap.cores < COMFORTABLE_CORES) lower('medium', `${cap.cores} cores`)
  if (cap.deviceMemoryGb !== undefined && cap.deviceMemoryGb < COMFORTABLE_MEMORY_GB) {
    lower('medium', `${cap.deviceMemoryGb} GB device memory`)
  }
  if (cap.coarsePointer) lower('medium', 'touch device — thermal headroom')

  return { ceiling, reasons }
}

/**
 * How much of a tier's frame budget a calibration must leave unspent to stay there.
 *
 * A measurement taken on a still machine at the moment of loading is the *best* frame this
 * device will ever draw: no balls in play, no multiball, no flashers, no jackpot. Choosing a
 * tier that exactly fits that frame is choosing a tier that misses every interesting one, so
 * the measurement has to fit inside two-thirds of the budget to be believed.
 */
export const CALIBRATION_HEADROOM = 0.67

/**
 * Would a frame this long be acceptable at this tier?
 *
 * **A predicate, not a ladder**, and the distinction is the whole reason this function has the
 * shape it does. `high`, `medium` and `low` all carry the same 16.7 ms frame budget, because
 * they all target sixty frames a second — what differs between them is not the target but what
 * they *spend* to reach it. So one measurement cannot rank them: a frame that misses `high` by
 * a millisecond misses `medium`'s identical budget by the same millisecond, and arithmetic over
 * the budget table would drop such a device straight to `low` having never once asked what it
 * could do at `medium`.
 *
 * The caller therefore measures at a tier, asks this, steps down, and **measures again** — the
 * only version of "choose a tier from a frame time" that is a measurement rather than a guess
 * about the relative cost of two things nobody timed. It costs a few frames on a device that
 * needs stepping down, at the one moment those frames are free: the machine is loading and
 * nobody is playing yet.
 *
 * A non-finite or non-positive measurement means the calibration did not happen (no context, a
 * lost context, a test with no GPU). That is not evidence of a slow device, so it fits.
 */
export function fitsFrameBudget(tier: RenderTier, frameMs: number): boolean {
  if (!Number.isFinite(frameMs) || frameMs <= 0) return true
  return frameMs <= TIER_BUDGETS[tier].frameBudgetMs * CALIBRATION_HEADROOM
}

/**
 * A short string that changes when the hardware does.
 *
 * 08 §6: "re-probe on a device change." A stored tier is a claim about a machine, and the
 * machine can change under the same browser profile — an external GPU unplugged, a driver
 * update that turns a blocklisted card back on, a laptop woken on a different dock. Comparing
 * fingerprints is how a stored answer finds out it is describing something else.
 *
 * Deliberately does **not** include the coarse-pointer flag: attaching a mouse to a tablet
 * changes that media query and does not change the device.
 */
export function deviceFingerprint(cap: Capability): string {
  return [
    cap.webgl2 ? 'gl2' : 'gl0',
    cap.maxTextureSize,
    cap.renderer,
    cap.deviceMemoryGb ?? '?',
    cap.cores ?? '?',
  ].join('|')
}
